import { formatters } from "@/utils/formatters";

/** Where an outbound message came from. */
export const ORIGIN_LABELS: Record<string, string> = {
  bot: "Bot",
  live_chat: "Live chat",
  api: "API",
  campaign: "Campaign",
  system: "System notices",
  notification: "Staff alerts",
  inbound: "Customers"
};

export const SENDER_LABELS: Record<string, string> = {
  bot: "Bot",
  agent: "Agent",
  ai_agent: "AI agent",
  system: "System",
  audience: "Customer"
};

/** Grouped content types, in the order the breakdown lists them. */
export const CONTENT_KIND_LABELS: Record<string, string> = {
  text: "Free-form text",
  template: "Template",
  interactive: "Buttons & lists",
  flow: "WhatsApp Flow",
  media: "Media",
  catalog: "Catalog & products",
  location: "Location",
  contact: "Contact card",
  order: "Order",
  reply: "Button/list/Flow reply",
  other: "Other"
};

export const TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  MARKETING: "Marketing",
  UTILITY: "Utility",
  AUTHENTICATION: "Authentication",
  UNKNOWN: "Unknown category"
};

/** How Meta prices a message. */
export const PRICING_CLASS_LABELS: Record<string, string> = {
  marketing: "Marketing templates",
  utility: "Utility templates",
  authentication: "Authentication templates",
  utility_in_window: "Utility in the 24h window",
  service: "Service (free-form replies)",
  free_entry_point: "Free entry point (ads)",
  unknown_template: "Templates, category unknown",
  not_charged: "Not charged"
};

/** One line each on why the class costs what it does. */
export const PRICING_CLASS_HINTS: Record<string, string> = {
  marketing: "Charged under both rule sets.",
  utility: "Charged under both rule sets.",
  authentication: "Charged under both rule sets.",
  utility_in_window: "Free until 1 Oct 2026, charged at the utility rate after.",
  service:
    "Free until 1 Oct 2026. Since then the first 1,000 a month per phone number are free, the rest at the utility rate.",
  free_entry_point: "Free for 72h after a Click-to-WhatsApp ad reply.",
  unknown_template: "Priced as marketing, the highest rate, until the category is known.",
  not_charged: ""
};

export const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sending: "Sending",
  completed: "Completed",
  failed: "Failed"
};

/** Meta error codes seen on failed sends, in plain words. */
export const META_ERROR_LABELS: Record<number, string> = {
  131026: "Undeliverable (no WhatsApp, old app, or blocked)",
  131047: "Outside the 24h window — needs a template",
  131049: "Meta limited marketing to this user",
  131048: "Spam rate limit hit",
  131050: "User stopped marketing messages",
  131051: "Unsupported message type",
  131053: "Media upload failed",
  130472: "User is in a Meta experiment",
  130429: "Throughput limit reached",
  131056: "Too many messages to this user",
  132000: "Template parameters don't match",
  132001: "Template doesn't exist or isn't approved",
  368: "Account restricted for policy violations"
};

export const labelOf = (labels: Record<string, string>, key: string) => labels[key] ?? key;

/** A sent-message row's name: templates carry their Meta category. */
export const sentTypeLabel = (kind: string, templateCategory: string | null): string =>
  kind === "template" && templateCategory
    ? `${CONTENT_KIND_LABELS.template} · ${labelOf(TEMPLATE_CATEGORY_LABELS, templateCategory)}`
    : labelOf(CONTENT_KIND_LABELS, kind);

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

/** "SA" → "Saudi Arabia"; `ZZ` → "Unknown"; `*` → "Rest of world". */
export const countryName = (code: string): string => {
  if (code === "ZZ") return "Unknown";
  if (code === "*") return "Rest of world";
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

export const formatUsd = (value: number) => formatters.formatCurrency(value, "USD");

/** One message's price: fractions of a cent, so four decimals. */
export const formatUnitPrice = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(value);

/** Ratios from the API are 0–1; null means "no data", shown as a dash. */
export const formatRatio = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : formatters.formatPercentage(value * 100);

/** Insights rates already arrive as percentages (0–100). */
export const formatPercent = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : formatters.formatPercentage(value);

/** Milliseconds as "45s", "12m", "3.2h". */
export const formatDuration = (ms: number | null | undefined) => {
  if (ms === null || ms === undefined) return "—";
  const seconds = ms / 1000;
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
};
